<script setup lang="ts">
import { MailIcon } from '@blpde/nx-icons';
import { computed, useTemplateRef, type InputHTMLAttributes } from 'vue';
import { isValidEmail } from './email-validation';
import type { BaseTextInputProps, InlineIconProps } from './internal';
import NxInput from './NxInput.vue';

// E-mail input: like `NxTextInput` plus a trailing "send mail" icon that opens a
// `mailto:` link. Validates client-side and exposes `isInvalid` (a computed)
// instead of rendering the error itself, so the consumer shows it — e.g. in
// their own `NxLoadingWrapper :error`, which an internal wrapper would clash
// with. The stored value is never rewritten.
const props = withDefaults(
  defineProps<
    BaseTextInputProps & {
      /** Accessible label / tooltip for the trailing mail icon button. */
      openLabel?: string;
    } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value'>
  >(),
  { openLabel: 'Send email' }
);

const [valueModel, valueModelModifiers] = defineModel<string, 'lazy'>({ required: true });

const hasValue = computed(() => (valueModel.value ?? '').trim().length > 0);

// The rule itself lives in `email-validation` so a consumer that REFUSES an invalid
// entry can apply the same check to the incoming value (it cannot use `isInvalid`
// for that — see the note there). Empty is allowed.
const isInvalid = computed(() => !isValidEmail(valueModel.value));

function openMailto() {
  const trimmed = (valueModel.value ?? '').trim();
  if (!trimmed) return;
  // window.location reliably hands off to the OS mail composer; window.open with
  // a mailto: scheme is flaky across browsers.
  window.location.href = `mailto:${trimmed}`;
}

// The trailing icon. With a value it is a clickable, brand-coloured button; when
// empty we omit `onClick`, so NxInputWrapper renders an inert, greyed <span> —
// visible but not clickable.
const mailIcons = computed<InlineIconProps[]>(() => [
  {
    component: MailIcon,
    align: 'end',
    filled: true,
    color: hasValue.value ? 'var(--nxt-brand-bg)' : 'var(--nxt-fg-muted)',
    label: props.openLabel,
    onClick: hasValue.value ? () => openMailto() : undefined,
    deco: false,
  },
]);

const inputRef = useTemplateRef('input-element');
const exposeRef = computed(() => {
  return inputRef.value?.inputElement;
});
// Expose the input element (focus) and `isInvalid` — read reactively via a
// template ref: `mailRef.value?.isInvalid`.
defineExpose({
  inputElement: exposeRef,
  isInvalid,
});
</script>

<template>
  <NxInput
    v-if="valueModelModifiers.lazy"
    :id="$props.id"
    ref="input-element"
    v-model.lazy="valueModel"
    type="text"
    inputmode="email"
    :icons="mailIcons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
  />
  <NxInput
    v-else
    :id="$props.id"
    ref="input-element"
    v-model="valueModel"
    type="text"
    inputmode="email"
    :icons="mailIcons"
    :wrapper-style="$props.wrapperStyle"
    :height="$props.height"
  />
</template>
